import { useEffect, useRef } from "react";
import * as THREE from "three";
import gsap from "gsap";

export default function BlueprintHover({ active, mouse }) {
  const mountRef = useRef(null);
  const groupRef = useRef(null);
  const mouseRef = useRef(mouse);
  mouseRef.current = mouse;

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.1, 50);
    camera.position.set(0, 0.6, 5.4);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const group = new THREE.Group();
    groupRef.current = group;
    const accent = new THREE.Color("#0162CA");
    const lineMat = new THREE.LineBasicMaterial({ color: accent, transparent: true, opacity: 0.9 });
    const faintMat = new THREE.LineBasicMaterial({ color: accent, transparent: true, opacity: 0.22 });

    const box = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(2.2, 1.4, 1.5)), lineMat);
    group.add(box);

    const roofPts = [];
    const c = [[-1.1, 0.7, -0.75], [-1.1, 0.7, 0.75], [1.1, 0.7, -0.75], [1.1, 0.7, 0.75]];
    const rL = [-1.1, 1.55, 0], rR = [1.1, 1.55, 0];
    roofPts.push(...c[0], ...rL, ...c[1], ...rL, ...c[2], ...rR, ...c[3], ...rR, ...rL, ...rR);
    const roofGeo = new THREE.BufferGeometry();
    roofGeo.setAttribute("position", new THREE.Float32BufferAttribute(roofPts, 3));
    group.add(new THREE.LineSegments(roofGeo, lineMat));

    const gridPts = [];
    for (let i = -1; i <= 1; i += 0.5) {
      gridPts.push(-1.1, i * 0.7, 0.751, 1.1, i * 0.7, 0.751);
      gridPts.push(i * 1.1, -0.7, 0.751, i * 1.1, 0.7, 0.751);
    }
    const gridGeo = new THREE.BufferGeometry();
    gridGeo.setAttribute("position", new THREE.Float32BufferAttribute(gridPts, 3));
    group.add(new THREE.LineSegments(gridGeo, faintMat));

    const doorPts = [-0.3, -0.7, 0.752, -0.3, 0.15, 0.752, -0.3, 0.15, 0.752, 0.3, 0.15, 0.752, 0.3, 0.15, 0.752, 0.3, -0.7, 0.752];
    const doorGeo = new THREE.BufferGeometry();
    doorGeo.setAttribute("position", new THREE.Float32BufferAttribute(doorPts, 3));
    group.add(new THREE.LineSegments(doorGeo, lineMat));

    group.position.y = -3.2;
    scene.add(group);

    let raf = 0;
    const clock = new THREE.Clock();
    const animate = () => {
      const t = clock.getElapsedTime();
      const m = mouseRef.current;
      group.rotation.y += ((m.x * 0.26 + t * 0) - group.rotation.y) * 0.08 + 0.003;
      group.rotation.x += ((m.y * 0.26) - group.rotation.x) * 0.08;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    const onResize = () => {
      if (!mount.clientWidth) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  useEffect(() => {
    if (!groupRef.current) return;
    gsap.to(groupRef.current.position, {
      y: active ? -0.1 : -3.2,
      duration: 0.6,
      ease: active ? "power3.out" : "power3.in",
    });
  }, [active]);

  return <div ref={mountRef} className="absolute inset-0 pointer-events-none" />;
}
